import { useQuery } from "@apollo/client";
import { Checkbox, ListSubheader, MenuItem } from "@mui/material";
import { Field, FieldProps } from "formik";
import { ReactElement, useEffect, useMemo } from "react";

import {
  ListTagsAndGroupsQuery,
  ListTagsAndGroupsQueryVariables,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import {
  MultiSelect,
  MultiSelectProps,
} from "~frontend/components/forms/FormSelect";
import CheckBoxUnchecked from "~frontend/components/icons/CheckBoxUnchecked";
import { LIST_TAGS_AND_GROUPS } from "~frontend/graphql/tags";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import formatTagName from "~frontend/util/formatTagName";

export default function TagMultiSelect(
  props: MultiSelectProps & {
    helperText?: string;
  }
): ReactElement {
  const { subaccount } = useSubaccount()!;
  const listTagsQuery = useQuery<
    ListTagsAndGroupsQuery,
    ListTagsAndGroupsQueryVariables
  >(LIST_TAGS_AND_GROUPS, {
    variables: {
      subaccountId: subaccount.id,
    },
  });

  const { tagNamesById, tagsAndGroups } = useMemo(() => {
    const tagNamesById: { [id: string]: string } = {};
    const tagsAndGroups: (
      | ListTagsAndGroupsQuery["listTags"][number]
      | ListTagsAndGroupsQuery["listTagGroups"][number]
    )[] = [];

    if (listTagsQuery.data) {
      for (const tag of listTagsQuery.data.listTags) {
        tagNamesById[tag.id] = tag.name;
        tagsAndGroups.push(tag);
      }

      for (const group of listTagsQuery.data.listTagGroups) {
        for (const tag of group.tags) {
          tagNamesById[tag.id] = formatTagName({
            name: tag.name,
            group,
          });
        }
        tagsAndGroups.push(group);
      }
    }

    tagsAndGroups.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

    return { tagNamesById, tagsAndGroups };
  }, [listTagsQuery.data]);

  const { name, disabled, value, onChange } = props;
  useEffect(() => {
    if (listTagsQuery.data && !disabled) {
      // make sure all selected tags are valid
      const selectedTagIds = value ?? [];
      const invalidTagIds = selectedTagIds.filter(
        tagId => !tagNamesById[tagId]
      );

      if (invalidTagIds.length > 0 && onChange) {
        const newSelectedTagIds = selectedTagIds.filter(
          tagId => !!tagNamesById[tagId]
        );

        onChange?.({
          target: { name, value: newSelectedTagIds },
        } as any);
      }
    }
  }, [listTagsQuery.data, name, disabled, value, onChange, tagNamesById]);

  if (!listTagsQuery.data) {
    return (
      <MultiSelect
        disabled
        {...props}
        renderValue={() =>
          listTagsQuery.error ? "Error loading tags" : "Loading..."
        }
      >
        <MenuItem disabled>
          <Loading />
        </MenuItem>
      </MultiSelect>
    );
  }

  return (
    <MultiSelect
      {...props}
      renderValue={() => {
        return (props.value ?? [])
          .map((tagId: string) => {
            return tagNamesById[tagId] ?? "Deleted Tag";
          })
          .join(", ");
      }}
    >
      {tagsAndGroups.map(tagOrGroup =>
        tagOrGroup.__typename === "TagGroup" ? (
          [
            <ListSubheader>{tagOrGroup.name}</ListSubheader>,
            ...tagOrGroup.tags.map(tag => (
              <MenuItem key={tag.id} value={tag.id} sx={{ pl: 4 }}>
                <Checkbox
                  checked={(props.value ?? []).includes(tag.id)}
                  icon={<CheckBoxUnchecked />}
                />
                {tag.name}
              </MenuItem>
            )),
          ]
        ) : (
          <MenuItem key={tagOrGroup.id} value={tagOrGroup.id}>
            <Checkbox
              checked={(props.value ?? []).includes(tagOrGroup.id)}
              icon={<CheckBoxUnchecked />}
            />
            {tagOrGroup.name}
          </MenuItem>
        )
      )}
    </MultiSelect>
  );
}

export function FormikTagMultiSelect({
  name,
  ...selectProps
}: Omit<MultiSelectProps, "onChange" | "value"> & {
  name: string;
}): ReactElement {
  return (
    <Field name={name}>
      {({ field: { ...formikField }, meta }: FieldProps) => {
        const error = meta.touched ? meta.error : null;
        return (
          <TagMultiSelect
            {...selectProps}
            {...formikField}
            error={!!error}
            helperText={error ?? undefined}
          />
        );
      }}
    </Field>
  );
}
